import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Shell from "@/components/Shell";
import { Tv, PlayCircle, Youtube, Radio, ShieldCheck, Users } from "lucide-react";

function embedUrl(url) {
  if (!url) return null;
  const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function Player({ active, onClose }) {
  if (!active) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" onClick={onClose}>
      <div className="max-w-3xl w-full space-y-2" onClick={e => e.stopPropagation()} data-testid="stv-player">
        <div className="aspect-video bg-black rounded-xl overflow-hidden border-2 border-white/20">
          {embedUrl(active.external_url) ? <iframe title={active.title} src={embedUrl(active.external_url)} className="w-full h-full" allowFullScreen />
            : active.media_url ? <video src={active.media_url} controls className="w-full h-full" />
              : <div className="grid place-items-center h-full opacity-60 text-white"><Youtube size={48} /></div>}
        </div>
        <div className="flex items-center justify-between text-white"><div className="font-bold">{active.title}</div>
          <button className="nb-btn nb-btn-danger text-xs" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function VideoGrid({ items, onPlay, emptyMsg }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map(c => (
        <button key={c.content_id} onClick={() => onPlay(c)} className="text-left rounded-xl overflow-hidden bg-[#0F1020] text-white border-2 border-black hover:-translate-y-1 transition-transform" data-testid={`stv-content-${c.content_id}`}>
          <div className="h-32 bg-black/60 grid place-items-center">
            {c.thumbnail_url ? <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" /> : <PlayCircle size={38} className="opacity-60" />}
          </div>
          <div className="p-3"><div className="font-bold text-sm line-clamp-2">{c.title}</div>
            <div className="text-xs opacity-60 mt-1">{c.board} {c.grade} · {c.subject}</div></div>
        </button>
      ))}
      {items.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">{emptyMsg}</div>}
    </div>
  );
}

function PonderrChannel({ ctx, onPlay }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/student/ponderrtv/official").then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      <div className="nb-card bg-[#93C5FD] p-3 text-sm flex items-center gap-2"><ShieldCheck size={16} /> Official Ponderr lessons & live streams pushed for <b>{ctx?.grade || "your grade"}</b>.</div>
      <VideoGrid items={items} onPlay={onPlay} emptyMsg="No official broadcasts pushed to your grade yet." />
    </div>
  );
}

function CommunityChannels({ ctx, onPlay }) {
  const [channels, setChannels] = useState([]);
  const [content, setContent] = useState([]);
  useEffect(() => {
    api.get("/tv/creators", { params: { board: ctx?.board_code || undefined } }).then(r => setChannels(r.data)).catch(() => {});
    api.get("/tv/content", { params: { board: ctx?.board_code || undefined, grade: ctx?.grade || undefined } }).then(r => setContent(r.data)).catch(() => {});
  }, [ctx]);
  return (
    <div className="space-y-4">
      <div className="nb-card bg-[#C4B5FD] p-3 text-sm">Approved creator channels matched to <b>{ctx?.board_code || "your board"} · {ctx?.grade || "your grade"}</b>.</div>
      {channels.length > 0 && (
        <div>
          <h3 className="display text-base font-bold mb-2 flex items-center gap-2"><Users size={16} /> Channels</h3>
          <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
            {channels.map(ch => (
              <Link key={ch.creator_id} to={`/tv/channel/${ch.creator_id}`} className="min-w-[210px] nb-card bg-white p-3 hover:-translate-y-1 transition-transform" data-testid={`stv-channel-${ch.creator_id}`}>
                <div className="w-10 h-10 bg-[#0F1020] text-white border-2 border-black rounded grid place-items-center mb-2"><Tv size={18} /></div>
                <div className="font-bold text-sm flex items-center gap-1">{ch.display_name}{ch.is_kids_approved && <span className="nb-badge bg-[#86EFAC] text-[9px]">KIDS</span>}</div>
                <div className="text-xs opacity-60">{(ch.boards || []).join(", ")} · {ch.content_count} videos</div>
              </Link>
            ))}
          </div>
        </div>
      )}
      <VideoGrid items={content} onPlay={onPlay} emptyMsg="No community lessons for your grade yet." />
    </div>
  );
}

export function PonderrTVPanel() {
  const [ctx, setCtx] = useState(null);
  const [sub, setSub] = useState("ponderr");
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/student/discovery-context").then(r => setCtx(r.data)).catch(() => setCtx({})); }, []);
  const subs = [
    { id: "ponderr", label: "Ponderr Channel", icon: ShieldCheck },
    { id: "community", label: "Community Channels", icon: Users },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Tv size={20} /><h2 className="display text-2xl font-bold">Ponderr TV</h2>
        {ctx?.board_code && <span className="nb-badge bg-white text-xs">{ctx.board_code} · {ctx.grade}</span>}
      </div>
      <div className="flex gap-2">
        {subs.map(s => (
          <button key={s.id} onClick={() => setSub(s.id)} data-testid={`stv-subtab-${s.id}`}
            className={`nb-btn text-xs ${sub === s.id ? "" : "nb-btn-ghost"}`}><s.icon size={14} /> {s.label}</button>
        ))}
      </div>
      {ctx && sub === "ponderr" && <PonderrChannel ctx={ctx} onPlay={setActive} />}
      {ctx && sub === "community" && <CommunityChannels ctx={ctx} onPlay={setActive} />}
      <Player active={active} onClose={() => setActive(null)} />
    </div>
  );
}

export default function StudentPonderrTV() {
  const { user } = useAuth();
  return (
    <Shell title="Ponderr TV" subtitle={user?.name}>
      <PonderrTVPanel />
    </Shell>
  );
}
